import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Cosine function parameters: a * cos(b(x - c)) + d */
export interface CosineParams {
  /** Amplitude (vertical stretch) */
  a: number;
  /** Frequency (affects period = 2π/b) */
  b: number;
  /** Phase shift (horizontal translation) */
  c: number;
  /** Vertical shift */
  d: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for cosine functions.
 * Handle is placed at the first maximum (c, a + d) since cos(0) = 1.
 * This makes the handle visually sit on the curve at the peak.
 */
export const COSINE_META: FunctionMeta<CosineParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'extrema',
    'tangent',
    'secant',
    'derivative',
    'axisOfSymmetry',
    'inflection',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'amplitude',
    'wavelength',
    'period',
    'phaseShift',
  ],
  handleConfigs: {
    translate: {
      xParam: 'c',
      yParam: 'd',
      // Handle at first maximum: (c, a + d)
      toPosition: (p) => [p['c'], p['a'] + p['d']],
      // When dragged to (x, y): c = x, d = y - a
      fromPosition: (x, y, p) => ({ c: x, d: y - p['a'] }),
    },
    'translate-x': {
      xParam: 'c',
      yParam: 'd',
      toPosition: (p) => [p['c'], p['a'] + p['d']],
      fromPosition: (x, _y, p) => ({ c: x, d: p['d'] }),
    },
    'translate-y': {
      xParam: 'c',
      yParam: 'd',
      toPosition: (p) => [p['c'], p['a'] + p['d']],
      fromPosition: (_x, y, p) => ({ c: p['c'], d: y - p['a'] }),
    },
  },
  defaultParams: {
    a: 1,
    b: 1,
    c: 0,
    d: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [0.1, 5],
    c: [-10, 10],
    d: [-10, 10],
  },
};
